// systemd journald parser
//
// The service-manager agent's journal pump (pkg/servicemanager/agent/journal.go) publishes one
// journald JSON record per Buffer into a NATS stream channel named "journal"
// (BufferSource.journalctl). Each Buffer's data is a complete `journalctl -o
// json` document — no embedded newlines, no continuation semantics. The shape
// (journald exports every field as a string) is:
//
//   {
//     "MESSAGE":              "nginx started",
//     "PRIORITY":             "6",                  // syslog severity 0-7
//     "_SYSTEMD_UNIT":        "nginx.service",      // or _SYSTEMD_USER_UNIT
//     "_PID":                 "1234",
//     "__REALTIME_TIMESTAMP": "1718736000000000",   // microseconds since epoch
//     ...many more _-prefixed trusted fields
//   }
//
// Exactly like the Windows Event Log case, the structured (accur8) parser
// treats anything without a leading HH:MM:SS timestamp as a continuation, so it
// concatenates every JSON record into one giant entry — the symptom is "the
// journal log viewer shows one smashed line of raw JSON despite many records."
// This parser sidesteps that: one record in, one LogEntry out, with the human
// MESSAGE surfaced and the full record kept in metadata.

import { LogEntry, LogLevel } from './types';

let logIdCounter = 0;

interface JournalRecord {
  MESSAGE?: unknown; // string, or number[] for non-UTF8 payloads
  PRIORITY?: string;
  SYSLOG_IDENTIFIER?: string;
  _SYSTEMD_UNIT?: string;
  _SYSTEMD_USER_UNIT?: string;
  _PID?: string;
  __REALTIME_TIMESTAMP?: string;
}

// journald PRIORITY is the syslog severity (RFC 5424): 0=emerg … 7=debug.
function mapPriority(priority: string | undefined): LogLevel | undefined {
  switch (priority) {
    case '0': // emerg
    case '1': // alert
    case '2': // crit
      return 'FATAL';
    case '3': // err
      return 'ERROR';
    case '4': // warning
      return 'WARN';
    case '5': // notice
    case '6': // info
      return 'INFO';
    case '7': // debug
      return 'DEBUG';
    default:
      return undefined;
  }
}

// MESSAGE is usually a string; for non-UTF8 payloads journald emits a byte
// array (number[]). Decode that to text; otherwise stringify defensively.
function decodeMessage(message: unknown): string {
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) {
    try {
      return new TextDecoder().decode(Uint8Array.from(message as number[]));
    } catch {
      return '';
    }
  }
  if (message == null) return '';
  return String(message);
}

/**
 * Create a journald parser. One Buffer = one JSON record = one LogEntry. No
 * continuation handling; each line is parsed in isolation (mirrors the Windows
 * Event Log parser).
 */
export function createJournalParser(sourceId?: string) {
  return {
    parseLine(rawLine: string, blockTimestamp?: Date): LogEntry[] {
      const line = rawLine.trim();
      if (!line) return [];

      let rec: JournalRecord;
      try {
        rec = JSON.parse(line);
      } catch {
        // Malformed JSON — surface the raw text so an upstream problem is visible.
        return [{
          id: `${sourceId || 'journal'}-${++logIdCounter}`,
          timestamp: blockTimestamp || new Date(),
          originalLine: rawLine,
          message: rawLine,
          level: 'ERROR',
          source: sourceId,
          isMultiLine: false,
        }];
      }

      const message = decodeMessage(rec.MESSAGE);
      const unit = rec._SYSTEMD_USER_UNIT || rec._SYSTEMD_UNIT;
      const ident = rec.SYSLOG_IDENTIFIER || unit;

      // __REALTIME_TIMESTAMP is microseconds since the epoch (as a string).
      let timestamp: Date | undefined;
      if (rec.__REALTIME_TIMESTAMP) {
        const us = Number(rec.__REALTIME_TIMESTAMP);
        if (Number.isFinite(us) && us > 0) {
          timestamp = new Date(us / 1000);
        }
      }
      if (!timestamp) timestamp = blockTimestamp || new Date();

      return [{
        id: `${sourceId || 'journal'}-${++logIdCounter}`,
        timestamp,
        originalLine: rawLine,
        message: message || `(${ident ?? 'journal'} record)`,
        category: unit,
        thread: ident,
        level: mapPriority(rec.PRIORITY),
        source: sourceId,
        isMultiLine: false,
        metadata: rec,
      }];
    },

    flush(): LogEntry[] {
      return [];
    },

    reset() {
      // no-op — every record is self-contained
    },
  };
}
